import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const ICON = {
  folder: '📁',
  file: {
    pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
    ppt: '📊', pptx: '📊', jpg: '🖼️', jpeg: '🖼️', png: '🖼️',
    gif: '🖼️', mp4: '🎬', mp3: '🎵', zip: '🗜️', txt: '📃',
  },
};

function fileIcon(name) {
  const ext = name.split('.').pop().toLowerCase();
  return ICON.file[ext] || '📎';
}

function fileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function Documents() {
  const [status, setStatus] = useState(null);
  const [path, setPath] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([{ name: 'FamilyOS', path: '' }]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [connectMsg, setConnectMsg] = useState('');

  useEffect(() => {
    api.get('/dropbox/status').then(r => setStatus(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get('dropbox_connected')) {
      setConnectMsg('✅ Dropbox connecté !');
      setStatus({ connected: true });
      window.history.replaceState({}, '', '/documents');
    }
    if (params.get('dropbox_error')) {
      setConnectMsg('❌ Erreur de connexion Dropbox');
      window.history.replaceState({}, '', '/documents');
    }
  }, []);

  const loadFolder = useCallback(async (folderPath, crumb) => {
    setLoading(true);
    setSearchResults(null);
    setSearchQ('');
    try {
      const { data } = await api.get('/dropbox/files', { params: { path: folderPath } });
      setEntries(data.entries || []);
      setPath(folderPath);
      if (crumb) setBreadcrumb(crumb);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (status?.connected) loadFolder('', [{ name: 'Dropbox', path: '' }]);
  }, [status?.connected, loadFolder]);

  const openFolder = (entry) => {
    const newCrumb = [...breadcrumb, { name: entry.name, path: entry.path }];
    loadFolder(entry.path, newCrumb);
  };

  const goToCrumb = (idx) => {
    const crumb = breadcrumb.slice(0, idx + 1);
    loadFolder(breadcrumb[idx].path, crumb);
  };

  const openFile = async (entry) => {
    try {
      const { data } = await api.get('/dropbox/link', { params: { path: entry.path } });
      window.open(data.link, '_blank');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const doSearch = async () => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/dropbox/search', { params: { q: searchQ } });
      setSearchResults(data.matches || []);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
    setSearching(false);
  };

  const connectDropbox = async () => {
    try {
      const { data } = await api.get('/dropbox/auth-url');
      window.location.href = data.url;
    } catch (e) {
      setConnectMsg('❌ ' + (e.response?.data?.error || e.message));
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Dropbox ?')) return;
    await api.delete('/dropbox/disconnect');
    setStatus({ connected: false });
    setEntries([]);
    setConnectMsg('Dropbox déconnecté.');
  };

  const displayList = searchResults !== null ? searchResults : entries;

  return (
    <div>
      <div className="topbar">
        <div className="page-title">📁 Documents</div>
        {status?.connected && (
          <button className="btn btn-ghost btn-sm" onClick={disconnect} style={{ color: '#EF5350', fontSize: 12 }}>
            Déconnecter
          </button>
        )}
      </div>

      <div className="page" style={{ paddingTop: 0 }}>
        {connectMsg && (
          <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12,
            background: connectMsg.startsWith('✅') ? '#E8F5E9' : '#FFEBEE',
            color: connectMsg.startsWith('✅') ? '#2E7D32' : '#C62828', fontSize: 13, fontWeight: 600 }}>
            {connectMsg}
          </div>
        )}

        {!status?.connected ? (
          <div className="card" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>📦</div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Connecter Dropbox</div>
            <div className="text-muted" style={{ marginBottom: 24, lineHeight: 1.6 }}>
              Accédez à tous vos documents familiaux directement depuis FamilyOS.
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={connectDropbox}>
              🔗 Connecter Dropbox
            </button>
          </div>
        ) : (
          <>
            {status.account && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px',
                background: '#E8F5E9', borderRadius: 10, fontSize: 13, color: '#2E7D32' }}>
                ✅ Connecté en tant que <strong>{status.account}</strong>
              </div>
            )}

            {/* Search bar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                className="form-input"
                placeholder="Rechercher un document..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                style={{ flex: 1 }}
              />
              {searchQ && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setSearchQ(''); setSearchResults(null); }}
                  style={{ fontSize: 18, padding: '0 12px' }}>×</button>
              )}
              <button className="btn btn-primary btn-sm" onClick={doSearch} disabled={searching}
                style={{ whiteSpace: 'nowrap' }}>
                {searching ? '...' : '🔍'}
              </button>
            </div>

            {/* Breadcrumb */}
            {searchResults === null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12,
                fontSize: 13, flexWrap: 'wrap' }}>
                {breadcrumb.map((crumb, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span style={{ color: '#BBBBCC' }}>›</span>}
                    <button
                      onClick={() => goToCrumb(i)}
                      style={{ background: 'none', border: 'none', cursor: i === breadcrumb.length - 1 ? 'default' : 'pointer',
                        color: i === breadcrumb.length - 1 ? '#333' : '#6C63FF',
                        fontWeight: i === breadcrumb.length - 1 ? 700 : 400, padding: 0 }}>
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {searchResults !== null && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#888' }}>
                {searchResults.length} résultat{searchResults.length !== 1 ? 's' : ''} pour « {searchQ} »
              </div>
            )}

            {/* File list */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <span className="spinner" style={{ width: 32, height: 32, borderColor: 'rgba(108,99,255,0.2)', borderTopColor: '#6C63FF' }} />
              </div>
            ) : displayList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#BBBBCC' }}>
                {searchResults !== null ? 'Aucun résultat' : 'Dossier vide'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {displayList.map((entry, i) => (
                  <div
                    key={i}
                    className="card-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                    onClick={() => entry.type === 'folder' ? openFolder(entry) : openFile(entry)}
                  >
                    <span style={{ fontSize: 22, flexShrink: 0 }}>
                      {entry.type === 'folder' ? ICON.folder : fileIcon(entry.name)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {entry.name}
                      </div>
                      {entry.type !== 'folder' && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {fileSize(entry.size)}{entry.modified ? ` · ${new Date(entry.modified).toLocaleDateString('fr-FR')}` : ''}
                        </div>
                      )}
                    </div>
                    <span style={{ color: '#BBBBCC', fontSize: 18, flexShrink: 0 }}>
                      {entry.type === 'folder' ? '›' : '↗'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div style={{ height: 8 }} />
      </div>
    </div>
  );
}
